import { useState, useEffect, useCallback } from "react";
import api from "../services/api";

const PORTAL_COLOR = {
  idealista: "#ef4444",
  fotocasa: "#22c55e",
  pisos: "#3b82f6",
  habitaclia: "#f97316",
};

const POBLS_PRIN = ["Dénia", "La Xara", "El Verger", "Els Poblets", "Ondara", "Beniarbeig", "Pedreguer", "Pego"];
const POBLS_MAS = ["Jávea/Xàbia", "Gata de Gorgos", "Calpe/Calp", "Teulada", "Benissa", "Altea", "Benidorm", "Jalón/Xaló", "La Nucia", "Llíber", "Orba", "Parcent", "Murla", "Benidoleig"];
const TIPOS_INM = ["Piso", "Apartamento", "Chalet", "Villa", "Casa", "Ático", "Dúplex", "Finca", "Terreno", "Local"];
const EXTRAS_LIST = [
  ["piscina", "🏊 Piscina"],
  ["garaje", "🚗 Garaje"],
  ["terraza", "🌿 Terraza"],
  ["ascensor", "🛗 Ascensor"],
  ["amueblado", "🪑 Amueblado"],
  ["primera_linea", "🏖️ 1ª línea"],
  ["vista_mar", "🌊 Vista mar"],
  ["pista_padel", "🎾 Pádel"],
];

const EXTRAS_INIT = Object.fromEntries(EXTRAS_LIST.map(([k]) => [k, false]));

export const COINCIDENCIA_INIT = {
  texto: "",
  precio_min: "",
  precio_max: "",
  m2_min: "",
  m2_max: "",
  pobls: [],
  tipo_inmueble: "",
  solo_sin_grupo: false,
  portales: [],
  ...EXTRAS_INIT,
};

function Chip({ label, active, onClick, small, theme }) {
  const c = theme.colors;
  return (
    <button type="button" onClick={onClick} style={{
      padding: small ? "2px 6px" : "3px 8px",
      fontSize: small ? 9 : 10, fontWeight: 500, borderRadius: 6,
      border: `1px solid ${active ? c.chipActiveBorder : c.chipBorder}`,
      background: active ? c.chipActiveBg : c.chipBg,
      color: active ? c.chipActiveText : c.chipText,
      cursor: "pointer", userSelect: "none", lineHeight: 1.3, fontFamily: "inherit",
    }}>
      {label}
    </button>
  );
}

function FieldLabel({ children, c }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 600, color: c.textPrimary || c.accentBlue,
      marginBottom: 3,
    }}>
      {children}
    </div>
  );
}

export function coincidenciaDesdeAnuncio(p) {
  if (!p) return { ...COINCIDENCIA_INIT };
  const car = p.caracteristicas || {};
  const precio = Number(p.precio_numerico) || 0;
  const m2 = Number(car.superficie_m2 || p.superficie_m2) || 0;
  const pct = 0.12;
  const mun = String(p.municipio || "").replace(/\s*-.*$/, "").trim();
  const extras = {};
  EXTRAS_LIST.forEach(([k]) => {
    extras[k] = car[k] === true || car[k] === "true";
  });
  let tipo = String(car.tipo_propiedad || "").trim();
  if (!tipo && p.titulo) {
    const hit = TIPOS_INM.find((t) => p.titulo.toLowerCase().includes(t.toLowerCase()));
    if (hit) tipo = hit;
  }
  return {
    ...COINCIDENCIA_INIT,
    ...extras,
    texto: (p.direccion || p.titulo || "").split(/[,·]/)[0].trim().slice(0, 100),
    precio_min: precio ? String(Math.round(precio * (1 - pct))) : "",
    precio_max: precio ? String(Math.round(precio * (1 + pct))) : "",
    m2_min: m2 ? String(Math.round(m2 * 0.85)) : "",
    m2_max: m2 ? String(Math.round(m2 * 1.15)) : "",
    pobls: mun ? [mun] : [],
    tipo_inmueble: tipo,
  };
}

function buildPropParams(f, pg = 1, limit = 30) {
  const params = { page: pg, limit, order: "precio_asc" };
  if (f.texto?.trim()) params.texto = f.texto.trim();
  if (f.precio_min) params.precio_min = f.precio_min;
  if (f.precio_max) params.precio_max = f.precio_max;
  if (f.m2_min) params.m2_min = f.m2_min;
  if (f.m2_max) params.m2_max = f.m2_max;
  if (f.pobls?.length) params.poblacion = f.pobls.join(",");
  if (f.tipo_inmueble) params.tipo_inmueble = f.tipo_inmueble;
  if (f.portales?.length) params.portales = f.portales.join(",");
  if (f.solo_sin_grupo) params.sin_grupo = "1";
  EXTRAS_LIST.forEach(([k]) => {
    if (f[k]) params[k] = "true";
  });
  return params;
}

function buildGrupoParams(f) {
  const params = { min_portales: 1, limit: 30, page: 1 };
  if (f.texto?.trim()) params.q = f.texto.trim();
  if (f.pobls?.length) params.poblacion = f.pobls.join(",");
  if (f.precio_min) params.precio_min = f.precio_min;
  if (f.precio_max) params.precio_max = f.precio_max;
  if (f.m2_min) params.m2_min = f.m2_min;
  if (f.m2_max) params.m2_max = f.m2_max;
  if (f.tipo_inmueble) params.tipo_inmueble = f.tipo_inmueble;
  EXTRAS_LIST.forEach(([k]) => {
    if (f[k]) params[k] = "true";
  });
  return params;
}

export default function PanelCoincidenciasMercado({
  theme,
  origenIds = [],
  origenLabel = "",
  seedAnuncio = null,
  onVinculado,
  onOpenAd,
}) {
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const [filtros, setFiltros] = useState({ ...COINCIDENCIA_INIT });
  const [masPobls, setMasPobls] = useState(false);
  const [tab, setTab] = useState("anuncios");
  const [anuncios, setAnuncios] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [totalAnuncios, setTotalAnuncios] = useState(0);
  const [totalGrupos, setTotalGrupos] = useState(0);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const setF = (k, v) => setFiltros((prev) => ({ ...prev, [k]: v }));

  const togglePobl = (p) => {
    setFiltros((prev) => {
      const has = prev.pobls.includes(p);
      return { ...prev, pobls: has ? prev.pobls.filter((x) => x !== p) : [...prev.pobls, p] };
    });
  };

  const buscar = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const propParams = buildPropParams(filtros);
      const grpParams = buildGrupoParams(filtros);
      const [propRes, grpRes] = await Promise.all([
        api.get("/scraping/propiedades", { params: propParams }),
        api.get("/scraping/mercado/grupos", { params: grpParams }),
      ]);
      const excl = new Set(origenIds.map(String));
      setAnuncios((propRes.data?.items || []).filter((a) => !excl.has(String(a.id))));
      setTotalAnuncios(propRes.data?.total || 0);
      setGrupos(grpRes.data?.items || []);
      setTotalGrupos(grpRes.data?.total || 0);
    } catch (e) {
      setMsg(e.response?.data?.error || e.message || "Error al buscar");
      setAnuncios([]);
      setGrupos([]);
    } finally {
      setLoading(false);
    }
  }, [filtros, origenIds]);

  useEffect(() => {
    if (seedAnuncio?.id) {
      setFiltros(coincidenciaDesdeAnuncio(seedAnuncio));
    }
  }, [seedAnuncio?.id]);

  useEffect(() => {
    if (origenIds.length) buscar();
  }, [origenIds.join(","), buscar]);

  const vincularDestino = async (destino) => {
    if (!origenIds.length) {
      setMsg("Selecciona al menos un anuncio en el listado");
      return;
    }
    const key = `${destino.tipo}:${destino.id}`;
    setBusyId(key);
    setMsg(null);
    try {
      await api.post("/scraping/mercado/vincular-destino", {
        origen_ids: origenIds,
        destino,
      });
      setMsg("Vinculación guardada.");
      onVinculado?.();
      buscar();
    } catch (e) {
      setMsg(e.response?.data?.error || e.message || "No se pudo vincular");
    } finally {
      setBusyId(null);
    }
  };

  const inp = {
    width: "100%", height: 28, padding: "0 8px", borderRadius: 6, fontSize: 11,
    border: `1px solid ${c.border}`, background: c.bgInput || c.bgPanel,
    color: c.textPrimary, fontFamily: "inherit", boxSizing: "border-box",
  };

  const labelColor = isDark ? c.textPrimary : "#1d4ed8";

  return (
    <div style={{
      display: "flex", flexDirection: "column", height: "100%",
      borderLeft: `1px solid ${c.sectionBorder}`, background: c.bgPanel, minWidth: 0,
    }}>
      <div style={{ padding: "10px 12px", borderBottom: `1px solid ${c.sectionBorder}`, flexShrink: 0 }}>
        <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: c.textPrimary }}>
          Coincidencias
        </p>
        <p style={{ margin: "4px 0 0", fontSize: 10, color: c.textMuted, lineHeight: 1.4 }}>
          {origenIds.length
            ? `Origen: ${origenIds.length} anuncio${origenIds.length !== 1 ? "s" : ""}${origenLabel ? ` · ${origenLabel.slice(0, 50)}` : ""}`
            : "Selecciona anuncio(s) en el listado central"}
        </p>
      </div>

      <div style={{
        flexShrink: 0, borderBottom: `1px solid ${c.sectionBorder}`,
        maxHeight: "42vh", overflowY: "auto",
      }}>
        <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <FieldLabel c={{ ...c, textPrimary: labelColor }}>Población</FieldLabel>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {POBLS_PRIN.map((p) => (
                <Chip
                  key={p}
                  label={p}
                  active={filtros.pobls.includes(p)}
                  onClick={() => togglePobl(p)}
                  small
                  theme={theme}
                />
              ))}
              <button type="button" onClick={() => setMasPobls(!masPobls)} style={{
                padding: "2px 6px", fontSize: 9, fontWeight: 500, borderRadius: 6, cursor: "pointer",
                border: `1px solid ${masPobls ? c.accentBlueBorder : c.border}`,
                background: masPobls ? c.accentBlueLight : c.chipBg,
                color: masPobls ? c.accentBlue : c.textMuted,
                fontFamily: "inherit",
              }}>
                {masPobls ? "▾ Menos" : "▸ Más"}
              </button>
            </div>
            {masPobls && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                {POBLS_MAS.map((p) => (
                  <Chip
                    key={p}
                    label={p}
                    active={filtros.pobls.includes(p)}
                    onClick={() => togglePobl(p)}
                    small
                    theme={theme}
                  />
                ))}
              </div>
            )}
          </div>

          <div>
            <FieldLabel c={{ ...c, textPrimary: labelColor }}>Dirección</FieldLabel>
            <input
              value={filtros.texto}
              onChange={(e) => setF("texto", e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") buscar(); }}
              placeholder="Calle, título, referencia…"
              style={inp}
            />
          </div>

          <div>
            <FieldLabel c={{ ...c, textPrimary: labelColor }}>Tipo</FieldLabel>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {TIPOS_INM.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  active={filtros.tipo_inmueble === t}
                  onClick={() => setF("tipo_inmueble", filtros.tipo_inmueble === t ? "" : t)}
                  small
                  theme={theme}
                />
              ))}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <FieldLabel c={{ ...c, textPrimary: labelColor }}>Precio mín.</FieldLabel>
              <input
                type="number"
                value={filtros.precio_min}
                onChange={(e) => setF("precio_min", e.target.value)}
                placeholder="€"
                style={inp}
              />
            </div>
            <div>
              <FieldLabel c={{ ...c, textPrimary: labelColor }}>Precio máx.</FieldLabel>
              <input
                type="number"
                value={filtros.precio_max}
                onChange={(e) => setF("precio_max", e.target.value)}
                placeholder="€"
                style={inp}
              />
            </div>
            <div>
              <FieldLabel c={{ ...c, textPrimary: labelColor }}>m² mín.</FieldLabel>
              <input
                type="number"
                value={filtros.m2_min}
                onChange={(e) => setF("m2_min", e.target.value)}
                placeholder="0"
                style={inp}
              />
            </div>
            <div>
              <FieldLabel c={{ ...c, textPrimary: labelColor }}>m² máx.</FieldLabel>
              <input
                type="number"
                value={filtros.m2_max}
                onChange={(e) => setF("m2_max", e.target.value)}
                placeholder="0"
                style={inp}
              />
            </div>
          </div>

          <div>
            <FieldLabel c={{ ...c, textPrimary: labelColor }}>Extras</FieldLabel>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {EXTRAS_LIST.map(([k, l]) => (
                <Chip
                  key={k}
                  label={l}
                  active={filtros[k]}
                  onClick={() => setF(k, !filtros[k])}
                  small
                  theme={theme}
                />
              ))}
            </div>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, color: c.textSecondary, cursor: "pointer" }}>
            <input type="checkbox" checked={filtros.solo_sin_grupo} onChange={(e) => setF("solo_sin_grupo", e.target.checked)} />
            Solo anuncios sin grupo
          </label>
        </div>
      </div>

      <div style={{ padding: "8px 12px", flexShrink: 0, borderBottom: `1px solid ${c.sectionBorder}` }}>
        <button type="button" disabled={loading} onClick={buscar} style={{
          width: "100%", padding: "8px 12px", borderRadius: 8, border: "none", fontSize: 11, fontWeight: 700,
          background: c.accentBlue, color: "#fff", cursor: loading ? "wait" : "pointer", fontFamily: "inherit",
          opacity: loading ? 0.7 : 1,
        }}>
          {loading ? "Buscando…" : "Buscar coincidencias"}
        </button>
        {msg && (
          <p style={{
            margin: "6px 0 0", fontSize: 10,
            color: msg.includes("guardada") ? "#22c55e" : "#ef4444",
          }}>
            {msg}
          </p>
        )}
      </div>

      <div style={{ display: "flex", gap: 4, padding: "6px 12px", flexShrink: 0 }}>
        {[
          { id: "anuncios", label: `Anuncios (${totalAnuncios})` },
          { id: "grupos", label: `Grupos (${totalGrupos})` },
        ].map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} style={{
            flex: 1, padding: "5px 8px", borderRadius: 6, fontSize: 10, fontWeight: 600,
            border: `1px solid ${tab === t.id ? c.accentBlue : c.border}`,
            background: tab === t.id ? c.chipBg : "transparent",
            color: tab === t.id ? c.accentBlue : c.textMuted,
            cursor: "pointer", fontFamily: "inherit",
          }}>
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "8px 10px" }}>
        {tab === "anuncios" && (
          anuncios.length === 0 ? (
            <p style={{ fontSize: 11, color: c.textMuted, textAlign: "center", marginTop: 24 }}>
              {loading ? "…" : "Sin anuncios. Ajusta criterios y busca."}
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {anuncios.map((a) => {
                const foto = a.fotos?.[0];
                const busy = busyId === `anuncio:${a.id}`;
                return (
                  <div key={a.id} style={{
                    display: "flex", gap: 8, padding: 8, borderRadius: 8,
                    border: `1px solid ${c.border}`, background: c.propCardBg || c.bgCard,
                  }}>
                    <div style={{ width: 56, height: 42, borderRadius: 4, overflow: "hidden", flexShrink: 0, background: "#111" }}>
                      {foto ? <img src={foto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: c.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {a.titulo || a.direccion || "Sin título"}
                      </p>
                      <p style={{ margin: "2px 0", fontSize: 9, color: c.textMuted }}>
                        {[a.portal, a.precio_numerico && `${Number(a.precio_numerico).toLocaleString("es-ES")}€`, a.mercado_num_anuncios > 1 && `${a.mercado_num_anuncios} en grupo`].filter(Boolean).join(" · ")}
                      </p>
                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                        <button type="button" onClick={() => onOpenAd?.(a)} style={{
                          fontSize: 9, padding: "2px 6px", borderRadius: 4, border: `1px solid ${c.border}`,
                          background: "transparent", color: c.textSecondary, cursor: "pointer", fontFamily: "inherit",
                        }}>Ver</button>
                        <button type="button" disabled={!origenIds.length || busy} onClick={() => vincularDestino({ tipo: "anuncio", id: a.id })} style={{
                          fontSize: 9, padding: "2px 8px", borderRadius: 4, border: "none",
                          background: "#22c55e", color: "#fff", cursor: origenIds.length && !busy ? "pointer" : "not-allowed",
                          fontFamily: "inherit", fontWeight: 700, opacity: origenIds.length ? 1 : 0.5,
                        }}>
                          {busy ? "…" : "Vincular"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {tab === "grupos" && (
          grupos.length === 0 ? (
            <p style={{ fontSize: 11, color: c.textMuted, textAlign: "center", marginTop: 24 }}>
              {loading ? "…" : "Sin grupos."}
            </p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {grupos.map((g) => {
                const dc = g.datos_canonicos || {};
                const anunciosG = g.anuncios || [];
                const foto = anunciosG.find((x) => x.fotos?.length)?.fotos?.[0];
                const busy = busyId === `grupo:${g.id}`;
                return (
                  <div key={g.id} style={{
                    display: "flex", gap: 8, padding: 8, borderRadius: 8,
                    border: `1px solid ${c.border}`, background: c.propCardBg || c.bgCard,
                  }}>
                    <div style={{ width: 56, height: 42, borderRadius: 4, overflow: "hidden", flexShrink: 0, background: "#111" }}>
                      {foto ? <img src={foto} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : null}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 10, fontWeight: 600, color: c.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {dc.titulo || dc.direccion || "Grupo"}
                      </p>
                      <p style={{ margin: "2px 0", fontSize: 9, color: c.textMuted }}>
                        {[g.num_portales && `${g.num_portales} portales`, g.precio_minimo && `${Number(g.precio_minimo).toLocaleString("es-ES")}€`].filter(Boolean).join(" · ")}
                      </p>
                      <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginTop: 2 }}>
                        {anunciosG.slice(0, 4).map((a) => (
                          <span key={a.id} style={{
                            fontSize: 8, fontWeight: 700, color: "#fff", padding: "1px 5px", borderRadius: 4,
                            background: PORTAL_COLOR[a.portal] || "#64748b",
                          }}>{a.portal}</span>
                        ))}
                      </div>
                      <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
                        {anunciosG[0] && (
                          <button type="button" onClick={() => onOpenAd?.(anunciosG[0])} style={{
                            fontSize: 9, padding: "2px 6px", borderRadius: 4, border: `1px solid ${c.border}`,
                            background: "transparent", color: c.textSecondary, cursor: "pointer", fontFamily: "inherit",
                          }}>Ver</button>
                        )}
                        <button type="button" disabled={!origenIds.length || busy} onClick={() => vincularDestino({ tipo: "grupo", id: g.id })} style={{
                          fontSize: 9, padding: "2px 8px", borderRadius: 4, border: "none",
                          background: "#22c55e", color: "#fff", cursor: origenIds.length && !busy ? "pointer" : "not-allowed",
                          fontFamily: "inherit", fontWeight: 700, opacity: origenIds.length ? 1 : 0.5,
                        }}>
                          {busy ? "…" : "Vincular aquí"}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}
